"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isDatabaseProjectionFilterOperator = void 0;
exports.collectUnsupportedDatabaseFilterOperators = collectUnsupportedDatabaseFilterOperators;
exports.createDatabaseProjectionFilterEvaluator = createDatabaseProjectionFilterEvaluator;
const database_view_filter_1 = require("./database-view-filter");
const file_index_1 = require("./file-index");
/** Build columnId → (optionId → label) map for select/multi-select columns */
function buildOptionMaps(columns) {
    const maps = new Map();
    for (const col of columns) {
        if (col.type === 'select' || col.type === 'multi-select') {
            const options = col.data?.options || [];
            maps.set(col.id, new Map(options.map(o => [o.id, o.value])));
        }
    }
    return maps;
}
/**
 * Resolve a select option ID to its label for comparison.
 * Filter UI stores labels as args, but cell stores option IDs.
 */
function resolveSelectLabel(value, optionMap) {
    if (typeof value !== 'string')
        return String(value ?? '');
    return optionMap?.get(value) ?? value;
}
function selectValueMatches(actual, expected, optionMap) {
    return (String(actual) === String(expected) ||
        resolveSelectLabel(actual, optionMap) ===
            resolveSelectLabel(expected, optionMap));
}
const FILTER_FNS = {
    ...Object.fromEntries(Object.keys(database_view_filter_1.databaseViewFilterFunctions).map(name => [
        name,
        (self, args) => (0, database_view_filter_1.getDatabaseViewFilterFunction)(name)(self, ...args),
    ])),
    // Tag/Select filters — args[0] is an array of option IDs
    isOneOf: (self, args, optionMap) => {
        const allowed = Array.isArray(args[0]) ? args[0] : [];
        if (allowed.length === 0)
            return true;
        if (self == null)
            return false;
        return allowed.some(value => selectValueMatches(self, value, optionMap));
    },
    isNotOneOf: (self, args, optionMap) => {
        const blocked = Array.isArray(args[0]) ? args[0] : [];
        if (blocked.length === 0)
            return true;
        if (self == null)
            return true;
        return !blocked.some(value => selectValueMatches(self, value, optionMap));
    },
    containsOneOf: (self, args, optionMap) => {
        const allowed = Array.isArray(args[0]) ? args[0] : [];
        if (allowed.length === 0)
            return true;
        if (!Array.isArray(self))
            return false;
        return self.some(actual => allowed.some(expected => selectValueMatches(actual, expected, optionMap)));
    },
    doesNotContainOneOf: (self, args, optionMap) => {
        const blocked = Array.isArray(args[0]) ? args[0] : [];
        if (blocked.length === 0)
            return true;
        if (!Array.isArray(self))
            return true;
        return !self.some(actual => blocked.some(expected => selectValueMatches(actual, expected, optionMap)));
    },
    // Number filters
    '=': (self, args) => Number(self) === Number(args[0]),
    '≠': (self, args) => Number(self) !== Number(args[0]),
    '<': (self, args) => Number(self) < Number(args[0]),
    '>': (self, args) => Number(self) > Number(args[0]),
    '≤': (self, args) => Number(self) <= Number(args[0]),
    '≥': (self, args) => Number(self) >= Number(args[0]),
    // Boolean filters
    isTrue: self => self === true,
    isFalse: self => self !== true,
    // Empty checks
    isEmpty: (self, _args, _optionMap, column) => column?.type === 'attachment'
        ? (0, file_index_1.normalizeAttachmentCell)(self).isEmpty
        : self == null ||
            self === '' ||
            (Array.isArray(self) && self.length === 0),
    isNotEmpty: (self, _args, _optionMap, column) => column?.type === 'attachment'
        ? !(0, file_index_1.normalizeAttachmentCell)(self).isEmpty
        : self != null &&
            self !== '' &&
            !(Array.isArray(self) && self.length === 0),
    // Attachment filters
    filenameContains: (self, args, _optionMap, column) => {
        if (column?.type !== 'attachment')
            return false;
        const needle = String(args[0] ?? '').toLowerCase();
        return (0, file_index_1.normalizeAttachmentCell)(self).filenames.some(filename => filename.toLowerCase().includes(needle));
    },
    mimeStartsWith: (self, args, _optionMap, column) => {
        if (column?.type !== 'attachment')
            return false;
        const prefix = String(args[0] ?? '').toLowerCase();
        return (0, file_index_1.normalizeAttachmentCell)(self).mimeTypes.some(mime => mime.toLowerCase().startsWith(prefix));
    },
    countEqual: (self, args, _optionMap, column) => column?.type === 'attachment' &&
        (0, file_index_1.normalizeAttachmentCell)(self).count === Number(args[0]),
    countNotEqual: (self, args, _optionMap, column) => column?.type === 'attachment' &&
        (0, file_index_1.normalizeAttachmentCell)(self).count !== Number(args[0]),
    countGreaterThan: (self, args, _optionMap, column) => column?.type === 'attachment' &&
        (0, file_index_1.normalizeAttachmentCell)(self).count > Number(args[0]),
    countGreaterThanOrEqual: (self, args, _optionMap, column) => column?.type === 'attachment' &&
        (0, file_index_1.normalizeAttachmentCell)(self).count >= Number(args[0]),
    countLessThan: (self, args, _optionMap, column) => column?.type === 'attachment' &&
        (0, file_index_1.normalizeAttachmentCell)(self).count < Number(args[0]),
    countLessThanOrEqual: (self, args, _optionMap, column) => column?.type === 'attachment' &&
        (0, file_index_1.normalizeAttachmentCell)(self).count <= Number(args[0]),
};
function collectUnsupportedDatabaseFilterOperatorsAt(filter, path, unsupported) {
    if (typeof filter !== 'object' || filter === null) {
        return;
    }
    const record = filter;
    if (record.type === 'group') {
        const conditions = Array.isArray(record.conditions)
            ? record.conditions
            : [];
        conditions.forEach((condition, index) => collectUnsupportedDatabaseFilterOperatorsAt(condition, `${path}.conditions.${index}`, unsupported));
        return;
    }
    if (record.type !== 'filter') {
        return;
    }
    const operator = typeof record.function === 'string' ? record.function : undefined;
    if (operator && !FILTER_FNS[operator]) {
        unsupported.push({
            operator,
            path: `${path}.function`,
        });
    }
}
function collectUnsupportedDatabaseFilterOperators(filter) {
    const unsupported = [];
    collectUnsupportedDatabaseFilterOperatorsAt(filter, 'filter', unsupported);
    return unsupported;
}
/** Existing backend filter kernel, shared with bounded projection execution. */
function createDatabaseProjectionFilterEvaluator(columns, filter, context) {
    const options = buildOptionMaps([...columns]);
    const columnMap = new Map(columns.map(column => [column.id, column]));
    const evaluate = (node, readValue) => {
        if (node.type === 'group') {
            if (!node.conditions.length)
                return true;
            return node.op === 'and'
                ? node.conditions.every(child => evaluate(child, readValue))
                : node.conditions.some(child => evaluate(child, readValue));
        }
        const columnId = node.left?.name;
        const name = node.function;
        if (!columnId || !name)
            return true;
        const fn = FILTER_FNS[name];
        if (!fn)
            return true;
        const value = readValue(columnId) ?? null;
        const args = node.args.map(arg => arg.value);
        if (name === 'olderThanDays')
            return (0, database_view_filter_1.isOlderThanDays)(value, args[0], context.nowMs);
        return fn(value, args, options.get(columnId), columnMap.get(columnId));
    };
    return readValue => evaluate(filter, readValue);
}
const isDatabaseProjectionFilterOperator = (name) => Object.prototype.hasOwnProperty.call(FILTER_FNS, name);
exports.isDatabaseProjectionFilterOperator = isDatabaseProjectionFilterOperator;
